/**
 * Generic validation utility functions that can be used throughout the application
 * 
 * Used in:
 *   - Auth components for form validation
 *   - Any other components that need validation utilities
 */

/**
 * Basic email format validation for immediate UX feedback
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email format
 */
export const isValidEmailFormat = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Basic username format validation for immediate UX feedback
 * @param {string} username - Username to validate
 * @returns {boolean} True if valid username format
 */
export const isValidUsernameFormat = (username) => {
  const usernameRegex = /^[a-zA-Z0-9_]+$/;
  return usernameRegex.test(username);
};

/**
 * Basic full name format validation for immediate UX feedback
 * @param {string} fullName - Full name to validate
 * @returns {boolean} True if valid name format
 */
export const isValidFullNameFormat = (fullName) => {
  const nameRegex = /^[a-zA-Z\s\-']+$/;
  return nameRegex.test(fullName);
};

/**
 * Check if string contains only alphanumeric characters
 * @param {string} value - String to validate
 * @returns {boolean} True if alphanumeric only
 */
export const isAlphanumeric = (value) => {
  const alphanumericRegex = /^[a-zA-Z0-9]+$/;
  return alphanumericRegex.test(value);
};

/**
 * Check if string contains only letters and spaces
 * @param {string} value - String to validate
 * @returns {boolean} True if letters and spaces only
 */
export const isLettersAndSpaces = (value) => {
  const lettersSpacesRegex = /^[a-zA-Z\s]+$/;
  return lettersSpacesRegex.test(value);
};

/**
 * Check if string is a valid URL format
 * @param {string} url - URL to validate
 * @returns {boolean} True if valid URL format
 */
export const isValidUrlFormat = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};
